import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    makeStyles,
    Paper,
    Snackbar,
    Typography,
} from '@material-ui/core';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import commonStyles from 'config/commonStyles';
import tableMapsStore from 'store/tableMapsStore';
import { QrConflictError } from 'store/tableMapsStore/actionCreators';
import { TableMapDetails } from 'store/tableMapsStore/reducer';
import { ApplicationState } from 'store';
import { TableMapDraft, TableMapWizardProvider, useTableMapWizard } from './context';
import WizardStepper from './components/WizardStepper';
import BasicInformationStep from './steps/BasicInformationStep';
import MapConfigurationStep from './steps/MapConfigurationStep';
import TablePlacementStep from './steps/TablePlacementStep';
import TableDetailsStep from './steps/TableDetailsStep';
import ReviewStep, { isReviewReady } from './steps/ReviewStep';
import CreatedStep, { CreatedResult } from './steps/CreatedStep';

const TOTAL_STEPS = 6;

const useStyles = makeStyles({
    ...commonStyles,
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 32px 0',
        backgroundColor: theme.palette.common.white,
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.primary.dark,
        lineHeight: 1.2,
    },
    headerSubtitle: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginTop: 2,
        marginBottom: 12,
    },
    body: {
        flex: 1,
        overflow: 'auto',
        padding: '24px 32px',
    },
    card: {
        borderRadius: 8,
        overflow: 'visible',
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 32px',
        borderTop: `1px solid ${theme.palette.grey['300']}`,
        backgroundColor: theme.palette.common.white,
    },
    footerLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    footerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    backButton: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        fontWeight: 600,
        letterSpacing: 1.25,
    },
    cancelButton: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        fontWeight: 600,
        letterSpacing: 1.25,
    },
    nextButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
        letterSpacing: 1.25,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.secondary.main,
        },
    },
    loadingRoot: {
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.common.white,
    },
});

function isStepValid(stepIndex: number, draft: { name: string; isActive: boolean }): boolean {
    if (stepIndex === 0) {
        return draft.name.trim() !== '';
    }
    // Steps 2–6: entrance is optional on step 2, rest are placeholders
    return true;
}

interface WizardShellProps {
    isEdit: boolean;
    editId?: string;
    originalDetails?: TableMapDetails;
}

function WizardShell({ isEdit, editId, originalDetails }: WizardShellProps) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const { draft } = useTableMapWizard();
    const userProfile = useSelector((state: ApplicationState) => state.user.profile);
    const [stepIndex, setStepIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [createdResult, setCreatedResult] = useState<CreatedResult | null>(null);

    const currentStep = stepIndex + 1;
    const canAdvance = isStepValid(stepIndex, draft);

    function handleNext() {
        if (stepIndex < TOTAL_STEPS - 1 && canAdvance) {
            setStepIndex((prev) => prev + 1);
        }
    }

    function handleBack() {
        if (stepIndex > 0) {
            setStepIndex((prev) => prev - 1);
        }
    }

    function handleCancel() {
        history.push('/table-maps');
    }

    async function handleCreate() {
        setSaving(true);
        setSaveError(null);
        try {
            const createdAt = new Date().toISOString();
            const nameParts = [userProfile.firstName, userProfile.lastName].filter(Boolean);
            const createdBy =
                nameParts.length > 0
                    ? nameParts.join(' ')
                    : userProfile.email || i18n.t('tableMaps.wizard.created.unknownUser');

            const thunk = tableMapsStore.actionCreators.createTableMap({
                name: draft.name,
                entrance: draft.entrance,
                tables: draft.tables,
                gridEnabled: draft.gridEnabled,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const createdId = await (dispatch(thunk as any) as Promise<string>);

            const derivedIsActive = draft.tables.some((t) => t.isActive);
            setCreatedResult({
                id: createdId,
                name: draft.name,
                tablesCount: draft.tables.length,
                totalSeats: draft.tables.reduce((sum, t) => sum + t.seats, 0),
                status: derivedIsActive ? 'Active' : 'Inactive',
                isActive: derivedIsActive,
                createdAt,
                createdBy,
            });
            setStepIndex(5);
        } catch (err) {
            if (err instanceof QrConflictError) {
                setSaveError(
                    i18n.t('tableMaps.wizard.review.qrConflict', {
                        tables: err.qrConflictLabels.join(', '),
                    })
                );
            } else {
                setSaveError(i18n.t('tableMaps.wizard.review.createError'));
            }
        } finally {
            setSaving(false);
        }
    }

    async function handleUpdate() {
        if (!editId || !originalDetails) return;
        setSaving(true);
        setSaveError(null);
        try {
            const updatedAt = new Date().toISOString();
            const nameParts = [userProfile.firstName, userProfile.lastName].filter(Boolean);
            const createdBy =
                nameParts.length > 0
                    ? nameParts.join(' ')
                    : userProfile.email || i18n.t('tableMaps.wizard.created.unknownUser');

            const thunk = tableMapsStore.actionCreators.updateTableMap({
                id: editId,
                name: draft.name,
                entrance: draft.entrance,
                tables: draft.tables,
                gridEnabled: draft.gridEnabled,
                originalTables: originalDetails.tables,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (dispatch(thunk as any) as Promise<void>);

            const derivedIsActive = draft.tables.some((t) => t.isActive);
            setCreatedResult({
                id: editId,
                name: draft.name,
                tablesCount: draft.tables.length,
                totalSeats: draft.tables.reduce((sum, t) => sum + t.seats, 0),
                status: derivedIsActive ? 'Active' : 'Inactive',
                isActive: derivedIsActive,
                createdAt: updatedAt,
                createdBy,
            });
            setStepIndex(5);
        } catch (err) {
            if (err instanceof QrConflictError) {
                setSaveError(
                    i18n.t('tableMaps.wizard.review.qrConflict', {
                        tables: err.qrConflictLabels.join(', '),
                    })
                );
            } else {
                setSaveError(i18n.t('tableMaps.wizard.review.createError'));
            }
        } finally {
            setSaving(false);
        }
    }

    function renderStepPanel() {
        if (stepIndex === 0) {
            return <BasicInformationStep />;
        }
        if (stepIndex === 1) {
            return <MapConfigurationStep />;
        }
        if (stepIndex === 2) {
            return <TablePlacementStep />;
        }
        if (stepIndex === 3) {
            return <TableDetailsStep />;
        }
        if (stepIndex === 4) {
            return <ReviewStep />;
        }
        if (stepIndex === 5 && createdResult) {
            return <CreatedStep draft={draft} createdResult={createdResult} isEdit={isEdit} />;
        }
        return null;
    }

    const title = isEdit ? i18n.t('tableMaps.wizard.editTitle') : i18n.t('tableMaps.wizard.title');

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Typography className={classes.headerTitle}>{title}</Typography>
                <Typography className={classes.headerSubtitle}>
                    {i18n.t('tableMaps.wizard.stepOf', { n: currentStep })}
                </Typography>
                <WizardStepper
                    currentStep={currentStep}
                    onStepClick={
                        isEdit && stepIndex !== 5 ? (target) => setStepIndex(target) : undefined
                    }
                />
            </div>

            <div className={classes.body}>
                <Paper className={classes.card} elevation={2}>
                    {renderStepPanel()}
                </Paper>
            </div>

            <div className={classes.footer}>
                {stepIndex === 5 ? (
                    <>
                        <div className={classes.footerLeft}>
                            <Button
                                variant="outlined"
                                className={classes.backButton}
                                onClick={() => history.push('/table-maps')}
                            >
                                {i18n.t('tableMaps.wizard.created.backToTableMaps')}
                            </Button>
                        </div>
                        <div className={classes.footerRight}>
                            <Button
                                variant="contained"
                                className={classes.nextButton}
                                disabled={!createdResult}
                                onClick={() => {
                                    if (createdResult) {
                                        history.push(`/table-maps/${createdResult.id}`);
                                    }
                                }}
                            >
                                {i18n.t('tableMaps.wizard.created.openTableMap')}
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={classes.footerLeft}>
                            {stepIndex > 0 && (
                                <Button
                                    variant="outlined"
                                    className={classes.backButton}
                                    onClick={handleBack}
                                >
                                    {i18n.t('tableMaps.wizard.back')}
                                </Button>
                            )}
                        </div>
                        <div className={classes.footerRight}>
                            <Button
                                variant="outlined"
                                className={classes.cancelButton}
                                onClick={handleCancel}
                            >
                                {i18n.t('tableMaps.wizard.cancel')}
                            </Button>

                            {stepIndex < 4 && (
                                <Button
                                    variant="contained"
                                    className={classes.nextButton}
                                    onClick={handleNext}
                                    disabled={!canAdvance}
                                >
                                    {i18n.t('tableMaps.wizard.next')}
                                </Button>
                            )}

                            {stepIndex === 4 && (
                                <Button
                                    variant="contained"
                                    className={classes.nextButton}
                                    onClick={isEdit ? handleUpdate : handleCreate}
                                    disabled={!isReviewReady(draft) || saving}
                                >
                                    {isEdit
                                        ? i18n.t('tableMaps.wizard.saveChanges')
                                        : i18n.t('tableMaps.wizard.review.createTableMap')}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>

            <Snackbar
                open={saveError !== null}
                message={saveError}
                autoHideDuration={6000}
                onClose={() => setSaveError(null)}
            />
        </div>
    );
}

export default function CreateTableMapWizard() {
    const { id } = useParams<{ id?: string }>();
    const dispatch = useDispatch();
    const history = useHistory();
    const details = useSelector((state: ApplicationState) =>
        id ? state.tableMaps.details[id] : undefined
    );
    const [fetchDone, setFetchDone] = useState<boolean>(!id);

    useEffect(() => {
        if (id) {
            const thunk = tableMapsStore.actionCreators.getTableMapDetails(id);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (dispatch(thunk as any) as Promise<void>).then(() => setFetchDone(true));
        }
    }, [id, dispatch]);

    if (id) {
        if (!fetchDone) {
            return (
                <Box
                    display="flex"
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor={theme.palette.common.white}
                >
                    <CircularProgress />
                </Box>
            );
        }

        if (!details) {
            history.push('/table-maps');
            return null;
        }

        const prefillDraft: TableMapDraft = {
            name: details.name,
            isActive: details.isActive,
            gridEnabled: details.gridEnabled,
            entrance: details.entrance,
            tables: details.tables,
        };

        return (
            <TableMapWizardProvider initialDraft={prefillDraft}>
                <WizardShell isEdit={true} editId={id} originalDetails={details} />
            </TableMapWizardProvider>
        );
    }

    return (
        <TableMapWizardProvider>
            <WizardShell isEdit={false} />
        </TableMapWizardProvider>
    );
}
